import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Calendar,
  Layers,
  BookOpen,
  CheckCircle2,
  Clock,
  AlertCircle,
  RotateCcw,
  Building2,
  Filter,
  RefreshCw,
  Search,
  ChevronRight,
  UserCheck,
  Check,
  Sparkles
} from 'lucide-react';

interface EntryStatusRecord {
  school_id: number;
  school_name: string;
  school_code: string;
  block: string;
  month: string;
  class_id: number;
  class_name: string;
  stream: string;
  section: string;
  subject_id: number;
  subject_name: string;
  subject_code: string;
  students_eligible: number;
  marks_entered: number;
  absent: number;
  not_applicable: number;
  pending: number;
  entry_status: 'NOT STARTED' | 'IN PROGRESS' | 'SUBMITTED' | 'COMPLETED' | 'REOPENED';
  last_entry_by: string;
  last_entry_by_role: string;
  last_entry_date: string | null;
}

interface MatrixItem {
  subject_id: number;
  subject_name: string;
  subject_code: string;
  months: Record<string, {
    status: string;
    icon: string;
    total_marks: number;
    entered: number;
    absent: number;
    avg_pct: number | null;
  }>;
}

export const MarksEntryStatusModule: React.FC<{
  defaultMonth?: string;
  defaultBlock?: string;
  defaultSchoolId?: string | number;
  title?: string;
  onOpenStudent?: (studentId: number) => void;
}> = ({
  defaultMonth = 'August',
  defaultBlock = 'all',
  defaultSchoolId = 'all',
  title = 'Marks Entry Status & Submission Registry',
  onOpenStudent
}) => {
  const { user, fetchWithAuth } = useAuth();

  const [month, setMonth] = useState<string>(defaultMonth);
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [selectedStream, setSelectedStream] = useState<string>('all');
  const [selectedSubject, setSelectedSubject] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  const [records, setRecords] = useState<EntryStatusRecord[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [matrixData, setMatrixData] = useState<MatrixItem[]>([]);
  const [matrixMonths, setMatrixMonths] = useState<string[]>(['May', 'July', 'August', 'September']);
  const [loading, setLoading] = useState<boolean>(true);
  const [matrixClass, setMatrixClass] = useState<string>('1');

  const isSchool = user?.role === 'school' || user?.role === 'principal' || user?.role === 'teacher';
  const isBEO = user?.role === 'beo';
  const isDEO = user?.role === 'deo' || user?.role === 'admin';

  const loadEntryStatus = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({
        month,
        class_id: selectedClass,
        stream: selectedStream,
        subject_id: selectedSubject,
        status: statusFilter
      });

      if (defaultBlock !== 'all') q.set('block', defaultBlock);
      if (defaultSchoolId !== 'all') q.set('school_id', String(defaultSchoolId));

      const res = await fetchWithAuth(`/api/monthly-assessments/entry-status?${q.toString()}`);
      const json = await res.json();
      if (json.success) {
        setRecords(json.data?.records || []);
        setSummary(json.data?.summary || null);
      }

      // Load Matrix
      const mq = new URLSearchParams({
        class_id: matrixClass
      });
      if (defaultBlock !== 'all') mq.set('block', defaultBlock);
      if (defaultSchoolId !== 'all') mq.set('school_id', String(defaultSchoolId));

      const mRes = await fetchWithAuth(`/api/monthly-assessments/entry-matrix?${mq.toString()}`);
      const mJson = await mRes.json();
      if (mJson.success) {
        setMatrixData(mJson.data?.matrix || []);
        if (mJson.data?.months) setMatrixMonths(mJson.data.months);
      }
    } catch (e) {
      console.error('Error loading entry status:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntryStatus();
  }, [month, selectedClass, selectedStream, selectedSubject, statusFilter, matrixClass, defaultBlock, defaultSchoolId]);

  const filteredRecords = records.filter(r => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      r.school_name.toLowerCase().includes(term) ||
      r.school_code.toLowerCase().includes(term) ||
      r.subject_name.toLowerCase().includes(term) ||
      r.block.toLowerCase().includes(term) ||
      r.last_entry_by.toLowerCase().includes(term)
    );
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'SUBMITTED':
        return (
          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2 py-0.5 rounded border border-emerald-300">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
            <span>COMPLETED</span>
          </span>
        );
      case 'IN PROGRESS':
        return (
          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[11px] font-bold px-2 py-0.5 rounded border border-amber-300">
            <Clock className="w-3 h-3 text-amber-600" />
            <span>IN PROGRESS</span>
          </span>
        );
      case 'REOPENED':
        return (
          <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-[11px] font-bold px-2 py-0.5 rounded border border-purple-300">
            <RotateCcw className="w-3 h-3 text-purple-600" />
            <span>REOPENED</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-[11px] font-semibold px-2 py-0.5 rounded border border-slate-300">
            <AlertCircle className="w-3 h-3 text-slate-400" />
            <span>NOT STARTED</span>
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* ---------------------------------------------------- */}
      {/* 1. DYNAMIC SUBJECT × MONTH ENTRY MATRIX              */}
      {/* ---------------------------------------------------- */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-[#0F2C59] text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <div className="text-[11px] font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>Assessment Entry Matrix — Month-wise Subject Progress</span>
            </div>
            <h3 className="text-sm md:text-base font-bold">
              Dynamic Marks Entry Matrix (May, July, August, September)
            </h3>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex items-center bg-[#081B38] rounded p-0.5 border border-slate-700 text-xs">
              <button
                onClick={() => setMatrixClass('1')}
                className={`px-3 py-1 font-bold rounded transition ${matrixClass === '1' ? 'bg-amber-400 text-[#0F2C59]' : 'text-slate-300 hover:text-white'}`}
              >
                Class 10
              </button>
              <button
                onClick={() => setMatrixClass('2')}
                className={`px-3 py-1 font-bold rounded transition ${matrixClass === '2' ? 'bg-amber-400 text-[#0F2C59]' : 'text-slate-300 hover:text-white'}`}
              >
                Class 12
              </button>
            </div>
          </div>
        </div>

        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 uppercase font-bold border-b border-slate-200 text-[11px]">
                <tr>
                  <th className="py-2.5 px-4">Subject</th>
                  <th className="py-2.5 px-3">Code</th>
                  {matrixMonths.map(m => (
                    <th key={m} className="py-2.5 px-4 text-center font-black">
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium">
                {matrixData.map(item => (
                  <tr key={item.subject_id} className="hover:bg-slate-50 transition">
                    <td className="py-2.5 px-4 font-bold text-slate-900 flex items-center gap-2">
                      <BookOpen className="w-3.5 h-3.5 text-blue-600" />
                      <span>{item.subject_name}</span>
                    </td>
                    <td className="py-2.5 px-3 text-slate-500 font-mono">{item.subject_code}</td>
                    {matrixMonths.map(m => {
                      const col = item.months?.[m];
                      const icon = col?.icon || '○';
                      const status = col?.status || 'NOT_STARTED';
                      const avg = col?.avg_pct;

                      return (
                        <td key={m} className="py-2.5 px-4 text-center">
                          <div className="inline-flex flex-col items-center justify-center">
                            <span
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-black shadow-xs ${
                                icon === '✓'
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                  : icon === '◐'
                                  ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                  : 'bg-slate-100 text-slate-400 border border-slate-300'
                              }`}
                              title={`${item.subject_name} (${m}): ${status} - ${col?.entered || 0} entered, ${col?.absent || 0} absent`}
                            >
                              {icon}
                            </span>
                            {avg !== null && avg !== undefined && (
                              <span className="text-[10px] font-bold text-slate-600 mt-0.5">
                                {avg}%
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between text-[11px] text-slate-600 gap-2">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1 font-semibold text-emerald-800">
                <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-800 font-black inline-flex items-center justify-center text-xs">✓</span>
                <span>Completed</span>
              </span>
              <span className="flex items-center gap-1 font-semibold text-amber-800">
                <span className="w-4 h-4 rounded-full bg-amber-100 text-amber-800 font-black inline-flex items-center justify-center text-xs">◐</span>
                <span>In Progress</span>
              </span>
              <span className="flex items-center gap-1 font-semibold text-slate-600">
                <span className="w-4 h-4 rounded-full bg-slate-100 text-slate-500 font-black inline-flex items-center justify-center text-xs">○</span>
                <span>Not Started</span>
              </span>
              <span className="flex items-center gap-1 font-semibold text-purple-800">
                <span className="w-4 h-4 rounded-full bg-purple-100 text-purple-800 font-black inline-flex items-center justify-center text-xs">↻</span>
                <span>Reopened</span>
              </span>
            </div>
            <div className="text-[10px] text-slate-400">
              * Matrix is dynamically aggregated from actual D1 marks records
            </div>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* 2. MARKS ENTRY STATUS DETAILED REGISTRY TABLE        */}
      {/* ---------------------------------------------------- */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header & Controls */}
        <div className="p-4 border-b border-slate-200 space-y-3">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
            <div>
              <div className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                Compliance & Accountability Tracking
              </div>
              <h2 className="text-base font-bold text-[#0F2C59]">
                {title}
              </h2>
            </div>

            {/* Top KPIs */}
            {summary && (
              <div className="flex items-center gap-2">
                <div className="bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-300 text-right">
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Submission Rate</div>
                  <div className="text-base font-black text-blue-900">{summary.submission_pct}%</div>
                </div>
                <div className="bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-300 text-right">
                  <div className="text-[10px] text-emerald-800 font-bold uppercase">Marks Entered</div>
                  <div className="text-base font-black text-emerald-900">{summary.total_entered.toLocaleString()}</div>
                </div>
              </div>
            )}
          </div>

          {/* Filters Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 text-xs">
            {/* Month */}
            <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-300 px-2.5 py-1.5 rounded">
              <Calendar className="w-3.5 h-3.5 text-slate-500" />
              <span className="font-semibold text-slate-700">Month:</span>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="bg-transparent font-bold text-[#0F2C59] focus:outline-none w-full"
              >
                <option value="May">May</option>
                <option value="July">July</option>
                <option value="August">August</option>
                <option value="September">September</option>
              </select>
            </div>

            {/* Class */}
            <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-300 px-2.5 py-1.5 rounded">
              <Layers className="w-3.5 h-3.5 text-slate-500" />
              <span className="font-semibold text-slate-700">Class:</span>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="bg-transparent font-medium text-slate-900 focus:outline-none w-full"
              >
                <option value="all">All Classes</option>
                <option value="1">Class 10</option>
                <option value="2">Class 12</option>
              </select>
            </div>

            {/* Stream */}
            <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-300 px-2.5 py-1.5 rounded">
              <span className="font-semibold text-slate-700">Stream:</span>
              <select
                value={selectedStream}
                onChange={(e) => setSelectedStream(e.target.value)}
                className="bg-transparent font-medium text-slate-900 focus:outline-none w-full"
              >
                <option value="all">All Streams</option>
                <option value="Science">Science</option>
                <option value="Commerce">Commerce</option>
                <option value="Arts">Arts</option>
              </select>
            </div>

            {/* Subject */}
            <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-300 px-2.5 py-1.5 rounded">
              <BookOpen className="w-3.5 h-3.5 text-slate-500" />
              <span className="font-semibold text-slate-700">Subject:</span>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="bg-transparent font-medium text-slate-900 focus:outline-none w-full"
              >
                <option value="all">All Subjects</option>
                <option value="1">English</option>
                <option value="2">Mathematics</option>
                <option value="3">Science</option>
                <option value="4">Physics</option>
                <option value="5">Chemistry</option>
                <option value="6">Biology</option>
                <option value="7">Hindi</option>
                <option value="8">Social Science</option>
              </select>
            </div>

            {/* Status */}
            <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-300 px-2.5 py-1.5 rounded">
              <Filter className="w-3.5 h-3.5 text-slate-500" />
              <span className="font-semibold text-slate-700">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-transparent font-medium text-slate-900 focus:outline-none w-full"
              >
                <option value="all">All Status</option>
                <option value="COMPLETED">Completed</option>
                <option value="IN PROGRESS">In Progress</option>
                <option value="NOT STARTED">Not Started</option>
                <option value="REOPENED">Reopened</option>
              </select>
            </div>

            {/* Search & Refresh */}
            <div className="flex items-center gap-1.5">
              <div className="relative w-full">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search school / staff..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-7 pr-2 py-1.5 bg-slate-50 border border-slate-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={loadEntryStatus}
                className="p-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded text-slate-700 shrink-0"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 text-slate-700 uppercase font-bold border-b border-slate-200 text-[11px]">
              <tr>
                <th className="py-2.5 px-3.5">School</th>
                <th className="py-2.5 px-2.5">Month</th>
                <th className="py-2.5 px-2.5">Class</th>
                <th className="py-2.5 px-2.5">Stream</th>
                <th className="py-2.5 px-3">Subject</th>
                <th className="py-2.5 px-2.5 text-center">Eligible</th>
                <th className="py-2.5 px-2.5 text-center text-emerald-800">Entered</th>
                <th className="py-2.5 px-2.5 text-center text-rose-800">Absent</th>
                <th className="py-2.5 px-2.5 text-center text-amber-800">Pending</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3.5">Last Entry By</th>
                <th className="py-2.5 px-3.5">Last Entry Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-medium">
              {filteredRecords.length > 0 ? (
                filteredRecords.map((r, idx) => (
                  <tr key={`${r.school_id}-${r.month}-${r.class_id}-${r.subject_id}-${idx}`} className="hover:bg-slate-50 transition">
                    <td className="py-2.5 px-3.5 font-bold text-slate-900">
                      <div>{r.school_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{r.school_code} • {r.block}</div>
                    </td>
                    <td className="py-2.5 px-2.5 font-semibold text-slate-800">{r.month}</td>
                    <td className="py-2.5 px-2.5 font-semibold text-slate-800">{r.class_name}</td>
                    <td className="py-2.5 px-2.5 text-slate-600">{r.stream}</td>
                    <td className="py-2.5 px-3 font-semibold text-slate-800">
                      <div>{r.subject_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{r.subject_code}</div>
                    </td>
                    <td className="py-2.5 px-2.5 text-center font-bold text-slate-900">{r.students_eligible}</td>
                    <td className="py-2.5 px-2.5 text-center font-black text-emerald-700 bg-emerald-50/50">{r.marks_entered}</td>
                    <td className="py-2.5 px-2.5 text-center font-bold text-rose-700">{r.absent}</td>
                    <td className="py-2.5 px-2.5 text-center font-bold text-amber-700">
                      {r.pending > 0 ? (
                        <span className="text-amber-800 font-black">{r.pending}</span>
                      ) : (
                        <span className="text-slate-400 font-normal">0</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-center">{getStatusBadge(r.entry_status)}</td>
                    <td className="py-2.5 px-3.5">
                      <div className="font-semibold text-slate-900">{r.last_entry_by}</div>
                      <div className="text-[10px] text-slate-400 uppercase">{r.last_entry_by_role}</div>
                    </td>
                    <td className="py-2.5 px-3.5 text-slate-500 font-mono text-[11px]">
                      {r.last_entry_date ? new Date(r.last_entry_date).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} className="py-8 text-center text-slate-400">
                    <AlertCircle className="w-6 h-6 mx-auto text-slate-300 mb-1" />
                    <span>No marks entry records match the selected filters.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
